import { HttpClient } from "../api/httpclient.service";
import { useAudioStore } from '../../utils/audioStore'; 
import { useUserStore } from "../../utils/userStore";
import { getAudioUrl } from '../../configs/config';
import { isAppAudioEnabled } from "../../utils/audioManager";


export class AudioService extends HttpClient {
    private static currentAudio: HTMLAudioElement | null = null;
    private static currentRequestId: number = 0;
    private static currentPage: string | null = null;
    private static inFlightLessonAudio: Map<string, Promise<string[]>> = new Map();
    private static activeInteractionId: string | null = null;
    private static isAudioPaused: boolean = false;
    private static pausedTimestamp: number = 0;
    // Callback to resolve the current playback promise when paused (unblocks the for-loop)
    private static currentPlaybackResolve: (() => void) | null = null;
    private isAudioEnabled: boolean = true;

    constructor() {
        super();
    }

    public static setCurrentPage(pageName: string | null) {
        AudioService.currentPage = pageName;
    }

    // Check if audio is currently playing
    public static isPlaying(): boolean {
        return !!AudioService.currentAudio && 
               !AudioService.currentAudio.paused && 
               !AudioService.currentAudio.ended;
    }
 
    
    // Method to stop any currently playing audio
    public static stopAudio(): void {
        AudioService.currentRequestId++; // Increment to break any pending audio loops
        
        // Resolve any pending playback promise to unblock the for-loop
        if (AudioService.currentPlaybackResolve) {
            AudioService.currentPlaybackResolve();
            AudioService.currentPlaybackResolve = null;
        }
        if (AudioService.currentAudio) {
            AudioService.currentAudio.pause();
            AudioService.currentAudio.currentTime = 0;
            AudioService.currentAudio = null;
        }
        AudioService.pausedTimestamp = 0;
        AudioService.isAudioPaused = false;
        AudioService.activeInteractionId = null;
        useAudioStore.getState().setPlaybackState({ activeId: null, isPlaying: false, isPaused: false });
    }

    public static getCurrentAudio(): HTMLAudioElement | null {
        return AudioService.currentAudio;
    }

    public static pauseAudio(): void {
        if (AudioService.currentAudio && !AudioService.currentAudio.paused) {
            console.log('[AudioService] pauseAudio at currentTime:', AudioService.currentAudio.currentTime);
            AudioService.currentAudio.pause();
            AudioService.isAudioPaused = true;
            // Do NOT resolve currentPlaybackResolve here!
            // The for-loop Promise must stay pending so that when we resume
            // and audio finishes, 'ended' fires and resolves it naturally.
            useAudioStore.getState().setPlaybackState({
                activeId: AudioService.activeInteractionId,
                isPlaying: false,
                isPaused: true
            });
        }
    }

    public static resumeAudio(): void {
        console.log('[AudioService] resumeAudio called, currentAudio:', !!AudioService.currentAudio, 
                     'paused:', AudioService.currentAudio?.paused,
                     'isAudioPaused:', AudioService.isAudioPaused);
        if (AudioService.currentAudio && AudioService.isAudioPaused) {
            AudioService.isAudioPaused = false;
            // With blob URLs, audio.currentTime is preserved across pause/play natively.
            // Just call play() — it resumes from where it was paused.
            AudioService.currentAudio.play().then(() => {
                console.log('[AudioService] resumed playing at:', AudioService.currentAudio?.currentTime);
            }).catch(err => {
                console.warn('[AudioService] resume play error:', err);
            });
            useAudioStore.getState().setPlaybackState({
                activeId: AudioService.activeInteractionId,
                isPlaying: true,
                isPaused: false
            });
        }
    }

    public static getActiveInteractionId(): string | null {
        return AudioService.activeInteractionId;
    }

    public static isPausedForInteraction(interactionId: string): boolean {
        return AudioService.activeInteractionId === interactionId && AudioService.isAudioPaused;
    }

    public static isPlayingForInteraction(interactionId: string): boolean {
        return AudioService.activeInteractionId === interactionId && AudioService.isPlaying() && !AudioService.isAudioPaused;
    }

    // Instance methods calling static methods
    public stopAudio(): void {
        AudioService.stopAudio();
    }

    public pauseAudio(): void {
        AudioService.pauseAudio();
    }

    public resumeAudio(): void {
        AudioService.resumeAudio();
    }
    
    // Instance method to check if audio is playing
    public isPlaying(): boolean {
        return AudioService.isPlaying();
    }

    // Private method to handle the actual API call
    private async _fetchAudioFromApi(text: string): Promise<string> {
        try {
            // Call your API endpoint with the required parameters
            const response = await this.post('/audio/convert_to_audio/', { text });

            // Check if response contains audio data
            if (response && response.audio_data) {
                // Create audio source from base64 data
                const audioSrc = `data:audio/wav;base64,${response.audio_data}`;
                return audioSrc;
            }
            throw new Error('Audio data not found in API response');
        } catch (error) {
            console.error(`Error fetching audio from API for text "${text}":`, error);
            throw error;
        }
    }

    private async _fetchAudioBinaryRow(filename: string): Promise<string | null> {
        try {
            const response = await this.get(`/audio/get_audio_binary_raw/${filename}`);
    
            if (response?.audio_available === false || !response?.audio_base64) {
                console.warn(`Audio not available for filename "${filename}"`);
                return null;
            }
    
            const base64Data = response.audio_base64;
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
    
            const byteArray = new Uint8Array(byteNumbers);
            const audioBlob = new Blob([byteArray], { type: 'audio/wav' });
            const audioUrl = URL.createObjectURL(audioBlob);
            return audioUrl;
    
        } catch (error) {
            console.log(`Error fetching audio binary for key "${filename}":`, error);
            return null;
        }
    }

    public async fetchTextAudio(requestData: { text: string }): Promise<string> {
        const { getAudioSrc } = useAudioStore.getState();
        try {
            // Use the store's getAudioSrc, passing the text and the API fetching function
            const audioSrc = await getAudioSrc(requestData.text, this._fetchAudioFromApi.bind(this));
            return audioSrc;
        } catch (error) {
            console.error(`Error in fetchTextAudio service method for text "${requestData.text}":`, error);
            throw error; // Re-throw to be handled by the caller
        }
    }

    public async handleTextToAudio(text: string, isPlay: boolean = true): Promise<HTMLAudioElement | null> {
        try {
            const requestId = ++AudioService.currentRequestId;

            this.stopAudio();
            useAudioStore.getState().stopAudio();
            
            if (!isAppAudioEnabled()) {
                return null;
            }

            const requestData = { text };
            const audioSrc = await this.fetchTextAudio(requestData);

            if (requestId !== AudioService.currentRequestId) {
                console.log(`Skipping outdated audio for text "${text}"`);
                return null;
            }

            if (audioSrc && isPlay) {
                const audio = new Audio(audioSrc);
                useAudioStore.getState().setCurrentAudio(audio);
                // Add ended event listener to clean up
                audio.addEventListener('ended', () => {
                    AudioService.currentAudio = null;
                });
                
                audio.play().catch(error => {
                    console.error(`Error playing audio in handleTextToAudio for text "${text}":`, error);
                    AudioService.currentAudio = null;
                });

                AudioService.currentAudio = audio;
                return audio;
            }
        } catch (error) {
            console.error(`Error in handleTextToAudio for text "${text}":`, error);
        }
        return null;
    }

    public async fetchLessonAudio(text: string, interactionId?: string, lessonId?: string): Promise<string[]> {
        const key = lessonId && interactionId ? `${lessonId}_${interactionId}` : (interactionId || text);
        if (AudioService.inFlightLessonAudio.has(key)) {
            console.log(`[AudioService] Reusing in-flight lesson audio request for: ${key}`);
            return AudioService.inFlightLessonAudio.get(key)!;
        }

        const promise = (async () => {
            try {
                const response = await this.post('/audio/convert_lesson_audio/', { text });

                if (response?.status === 'success' && response.segments?.length > 0) {
                    return response.segments.map((segment: any) => {
                        const mimeType = segment.content_type || 'audio/mp3';
                        return `data:${mimeType};base64,${segment.audio_data}`;
                    });
                }
                throw new Error('No audio segments returned from lesson audio API');
            } catch (error) {
                console.error('Error fetching lesson audio:', error);
                throw error;
            } finally {
                AudioService.inFlightLessonAudio.delete(key);
            }
        })();

        AudioService.inFlightLessonAudio.set(key, promise);
        return promise;
    }

    /**
     * Convert a base64 data URI to a Blob Object URL for seekable audio playback.
     */
    private static dataUriToBlobUrl(dataUri: string): string {
        const parts = dataUri.split(',');
        const mimeMatch = parts[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'audio/mp3';
        const bstr = atob(parts[1]);
        const u8arr = new Uint8Array(bstr.length);
        for (let i = 0; i < bstr.length; i++) {
            u8arr[i] = bstr.charCodeAt(i);
        }
        return URL.createObjectURL(new Blob([u8arr], { type: mime }));
    }

    public async handleLessonTextToAudio(text: string, interactionId: string, lessonId?: string): Promise<HTMLAudioElement[] | null> {
        try {
            this.stopAudio();
            useAudioStore.getState().stopAudio();
            const requestId = ++AudioService.currentRequestId;
            AudioService.activeInteractionId = interactionId;
            AudioService.isAudioPaused = false;
            AudioService.pausedTimestamp = 0;

            if (!isAppAudioEnabled()) {
                return null;
            }

            const audioStore = useAudioStore.getState();
            const cacheKey = lessonId ? `${lessonId}_${interactionId}` : interactionId;
            const cachedSrcs = audioStore.getAudioByKey?.(cacheKey);
            
            let audioSources: string[];
            if (cachedSrcs) {
                console.log('Serving lesson audio from Zustand cache for interaction:', interactionId);
                audioSources = JSON.parse(cachedSrcs);
            } else {
                console.log('Fetching lesson audio from API for interaction:', interactionId);
                audioSources = await this.fetchLessonAudio(text, interactionId, lessonId);
                audioStore.setAudioByKey?.(cacheKey, JSON.stringify(audioSources));
            }

            if (requestId !== AudioService.currentRequestId) {
                console.log(`Skipping outdated lesson audio for interaction "${interactionId}"`);
                return null;
            }

            // Convert all data URIs to blob URLs upfront for seekable playback
            const blobUrls: string[] = audioSources.map(src => {
                if (src.startsWith('data:audio')) {
                    try {
                        return AudioService.dataUriToBlobUrl(src);
                    } catch (e) {
                        console.warn('Error converting data URI to blob URL:', e);
                        return src;
                    }
                }
                return src;
            });

            const audioElements: HTMLAudioElement[] = [];
            for (const blobUrl of blobUrls) {
                if (requestId !== AudioService.currentRequestId) {
                    break;
                }

                const audio = new Audio(blobUrl);
                audioElements.push(audio);
                audioStore.setCurrentAudio(audio);
                AudioService.currentAudio = audio;
                AudioService.isAudioPaused = false;
                audioStore.setPlaybackState({ activeId: interactionId, isPlaying: true, isPaused: false });

                await new Promise<void>((resolve) => {
                    // Store resolve so stopAudio() can unblock the loop when starting new audio
                    AudioService.currentPlaybackResolve = resolve;

                    audio.addEventListener('ended', () => {
                        AudioService.currentPlaybackResolve = null;
                        resolve();
                    });
                    audio.addEventListener('error', (e) => {
                        console.warn('Lesson audio element error:', e);
                        AudioService.currentPlaybackResolve = null;
                        resolve();
                    });
                    audio.play().catch(error => {
                        console.warn('Browser prevented automatic lesson audio playback:', error);
                        AudioService.currentPlaybackResolve = null;
                        resolve();
                    });
                });
            }

            // Audio finished naturally — clean up (only if this is still the active request)
            if (requestId === AudioService.currentRequestId) {
                AudioService.currentAudio = null;
                AudioService.currentPlaybackResolve = null;
                AudioService.activeInteractionId = null;
                audioStore.setPlaybackState({ activeId: null, isPlaying: false, isPaused: false });
            }

            return audioElements;
        } catch (error) {
            console.error(`Error in handleLessonTextToAudio for interaction "${interactionId}":`, error);
        }
        return null;
    }

    public async handleTextToAudioByKey(textKey: string, isPlay: boolean = true): Promise<HTMLAudioElement | null> {
        const currentPage = AudioService.currentPage;
        try {
            const requestId = ++AudioService.currentRequestId
            
            this.stopAudio();
            useAudioStore.getState().stopAudio();
            
            if (!isAppAudioEnabled()) {
                return null;
            }

            const audioStore = useAudioStore.getState();
            // Try to get audio from the store first
            //let audioSrc: any = audioStore.getAudioByKey?.(textKey);

            let audioSrc: any = getAudioUrl(textKey);

            if (!audioSrc && textKey) {
                try {
                    //audioSrc = await this._fetchAudioBinaryRow(textKey);
                    if (!audioSrc) {
                        console.log(`No audio returned for key: ${textKey}`);
                        return null;
                    }
                    audioStore.setAudioByKey?.(textKey, audioSrc);
                } catch (error) {
                    console.error(`Failed to fetch audio for key "${textKey}":`, error);
                    return null;
                }
            }

            if (requestId !== AudioService.currentRequestId) {
                console.log(`Skipping outdated audio for key "${textKey}"`);
                return null;
            }

            if (currentPage !== AudioService.currentPage) {
                console.log(`Audio for key "${textKey}" canceled because page changed.`);
                return null;
            }

            if (audioSrc && isPlay) {
                const audio = new Audio(audioSrc);
                useAudioStore.getState().setCurrentAudio(audio);
                audio.addEventListener('ended', () => {
                    AudioService.currentAudio = null;
                });

                audio.play().catch(error => {
                    console.error(`Error playing audio in handleTextToAudio for text "${textKey}":`, error);
                    AudioService.currentAudio = null;
                });

                AudioService.currentAudio = audio;
                return audio;
            }
        } catch (error) {
            console.error(`Error in handleTextToAudio for text "${textKey}":`, error);
        }
        return null;
    }

// Load audio files asynchronously without blocking render
    public async loadAudioFiles (audioKeys_by_pageName: string): Promise<void> {        
        
        var audioKeys = [""];

        if (audioKeys_by_pageName) {
            if (audioKeys_by_pageName === "ChooseExercise") {
                audioKeys = [
                    "chooseexercise_voice_exaplin",
                    "qrcode_voice_exaplin",
                    "goodexercisechoise"                       
                ];
            } else if (audioKeys_by_pageName === "questionsandstpes") {  
                audioKeys = [     
                    "exercisequestions_voice_exaplin",
                    "questionsteps_voice_exaplin",   
                ];
            }else if (audioKeys_by_pageName === "BeforeChat") {
                audioKeys = [
                    "chatpage_voice_exaplin1",
                    "chatpage_voice_exaplin2",
                    "chatpage_voice_exaplin3",
                    "chatpage_voice_exaplin4",
                    "chatpage_voice_exaplin5",
                    "chatpage_voice_exaplin6"
                ];
            }
        }       
        
        
        if(audioKeys && audioKeys.length > 0 && audioKeys.some(key => key.trim() !== "")) {
            try {
                
                Promise.all(
                    audioKeys.map(key => 
                    this.handleTextToAudioByKey(key, false)
                    )
                ).catch(error => console.error("Background audio loading error:", error));
                
                } catch (audioError) {
                console.error("Error loading audio:", audioError);
                }            
        }        
          
    }
};   

